import { PDFDocument, pushGraphicsState, popGraphicsState, rectangle, clip, endPath, type PDFImage, type PDFPage } from "pdf-lib";
import { stampOutputMetadata } from "./pdfMetadata";

/**
 * Images → PDF. Every image is first turned into upright pixels in a format PDFs can embed:
 *   - JPEGs with an EXIF orientation tag (every phone photo) are redrawn upright, because a PDF
 *     embeds the stored pixels and ignores the tag;
 *   - GIF / BMP / SVG / WebP / AVIF are drawn to a PNG (browsers decode them); HEIC is converted by
 *     the caller; TIFF is refused with a clear message;
 *   - plain JPEGs and PNGs are embedded untouched, so nothing is re-compressed.
 * One unreadable image is reported and skipped; it never fails the others.
 */

export class ImageReadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImageReadError";
  }
}

// ---------------------------------------------------------------------------------------------
// reading metadata (pure, testable in Node)
// ---------------------------------------------------------------------------------------------

/** EXIF orientation (1-8) of a JPEG, 1 when absent. */
export function readJpegOrientation(b: Uint8Array): number {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return 1;
  let i = 2;
  while (i + 4 < b.length) {
    if (b[i] !== 0xff) return 1;
    const marker = b[i + 1];
    if (marker === 0xd9 || marker === 0xda) return 1;
    const len = (b[i + 2] << 8) | b[i + 3];
    if (marker === 0xe1 && b[i + 4] === 0x45 && b[i + 5] === 0x78 && b[i + 6] === 0x69 && b[i + 7] === 0x66) {
      const t = i + 10; // start of the TIFF header
      const le = b[t] === 0x49;
      const u16 = (o: number) => (le ? b[o] | (b[o + 1] << 8) : (b[o] << 8) | b[o + 1]);
      const u32 = (o: number) => (le ? (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0 : ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0);
      const ifd = t + u32(t + 4);
      if (ifd + 2 > b.length) return 1;
      const n = u16(ifd);
      for (let k = 0; k < n; k++) {
        const e = ifd + 2 + k * 12;
        if (e + 12 > b.length) return 1;
        if (u16(e) === 0x0112) {
          const v = u16(e + 8);
          return v >= 1 && v <= 8 ? v : 1;
        }
      }
      return 1;
    }
    i += 2 + len;
  }
  return 1;
}

/** The resolution an image says it has, in dots per inch; null when it does not say. */
export function readImageDpi(b: Uint8Array): number | null {
  // PNG: pHYs chunk (pixels per metre)
  if (b[0] === 0x89 && b[1] === 0x50) {
    let i = 8;
    while (i + 12 <= b.length) {
      const len = ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
      const type = String.fromCharCode(b[i + 4], b[i + 5], b[i + 6], b[i + 7]);
      if (type === "pHYs" && b[i + 16] === 1) {
        const ppm = ((b[i + 8] << 24) | (b[i + 9] << 16) | (b[i + 10] << 8) | b[i + 11]) >>> 0;
        return ppm > 0 ? Math.round(ppm * 0.0254) : null;
      }
      if (type === "IDAT" || type === "IEND") return null;
      i += 12 + len;
    }
    return null;
  }
  // JPEG: JFIF density in the APP0 segment
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff && b[3] === 0xe0 && b[6] === 0x4a && b[7] === 0x46) {
    const units = b[13];
    const x = (b[14] << 8) | b[15];
    if (x > 0 && units === 1) return x;
    if (x > 0 && units === 2) return Math.round(x * 2.54);
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// making an image embeddable (browser)
// ---------------------------------------------------------------------------------------------

const isPng = (b: Uint8Array) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
const isJpeg = (b: Uint8Array) => b[0] === 0xff && b[1] === 0xd8;

function loadImg(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageReadError("could not be read"));
    };
    img.src = url;
  });
}

const canvasToBlob = (c: HTMLCanvasElement, type: string, q?: number) =>
  new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new ImageReadError("could not be converted"))), type, q));

/**
 * Returns a File that pdf-lib can embed and that is upright. Plain JPEG/PNG files are returned as they
 * are. Throws ImageReadError with a short reason ("TIFF is not supported", "could not be read").
 */
export async function normalizeImageFile(file: File): Promise<File> {
  const lower = file.name.toLowerCase();
  if (/image\/tiff/.test(file.type) || /\.tiff?$/.test(lower)) throw new ImageReadError("TIFF is not supported yet. Convert it to JPG or PNG first");
  const head = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
  if (isPng(head)) return file.type === "image/png" ? file : new File([file], file.name, { type: "image/png" });
  if (isJpeg(head)) {
    const orientation = readJpegOrientation(head);
    const typed = file.type === "image/jpeg" ? file : new File([file], file.name, { type: "image/jpeg" });
    if (orientation === 1) return typed;
    // Redraw upright. createImageBitmap with "from-image" applies the stored orientation; an <img> does too.
    const canvas = document.createElement("canvas");
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      canvas.getContext("2d")!.drawImage(bmp, 0, 0);
      bmp.close?.();
    } catch {
      const img = await loadImg(file);
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d")!.drawImage(img, 0, 0);
    }
    return new File([await canvasToBlob(canvas, "image/jpeg", 0.95)], file.name, { type: "image/jpeg" });
  }
  // GIF, BMP, SVG, WebP, AVIF, anything else the browser can decode: draw to a PNG.
  const svg = file.type === "image/svg+xml" || /\.svg$/.test(lower);
  const img = await loadImg(file);
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (svg) {
    // An SVG has no pixels of its own: draw it at a size that stays sharp on paper.
    if (!w || !h) {
      w = 1200;
      h = 900;
    } else if (w < 1600) {
      h = Math.round((h * 1600) / w);
      w = 1600;
    }
  }
  if (!w || !h) throw new ImageReadError("could not be read");
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  return new File([await canvasToBlob(canvas, "image/png")], file.name.replace(/\.[^.]+$/, "") + ".png", { type: "image/png" });
}

// ---------------------------------------------------------------------------------------------
// building the PDF
// ---------------------------------------------------------------------------------------------

export type PageSpec = "a4" | "letter" | "original" | { widthPt: number; heightPt: number };
export type MarginChoice = "none" | "small" | "normal";
export const MARGIN_PT: Record<MarginChoice, number> = { none: 0, small: 18, normal: 36 };
export const PRESET_PT = { a4: { w: 595.28, h: 841.89 }, letter: { w: 612, h: 792 } } as const;

export interface ImagePdfOptions {
  pageSize: PageSpec;
  margin: MarginChoice;
  /** "fit": whole image inside the page; "fill": cover the page, cropping the overflow. */
  fit: "fit" | "fill";
  /** For "original": pixels per inch to assume. "auto" uses the image's own resolution, else 96. */
  originalDpi: "auto" | 72 | 96 | 150 | 300;
  /** Two images per page: side by side (vertical divider) or stacked. */
  pair: null | "vertical" | "horizontal";
  title?: string;
}

export const DEFAULT_IMAGE_PDF_OPTIONS: ImagePdfOptions = { pageSize: "a4", margin: "normal", fit: "fit", originalDpi: "auto", pair: null };

export interface ImagePdfResult {
  bytes: Uint8Array;
  pages: number;
  images: number;
  skipped: { name: string; reason: string }[];
}

export interface ImagePdfHooks {
  isCurrent?: () => boolean;
  progress?: (current: number, total: number) => void;
  /** Overridable for tests. */
  normalize?: (f: File) => Promise<File>;
}

export class ImagePdfCancelled extends Error {
  constructor() {
    super("cancelled");
  }
}

interface Loaded {
  name: string;
  img: PDFImage;
  dpi: number | null;
}

/** Where an image goes inside a box: whole image (never enlarged) or covering the box. */
export function placeImage(box: { x: number; y: number; w: number; h: number }, imgW: number, imgH: number, fit: "fit" | "fill"): { x: number; y: number; w: number; h: number } {
  const scale = fit === "fill" ? Math.max(box.w / imgW, box.h / imgH) : Math.min(box.w / imgW, box.h / imgH, 1);
  const w = imgW * scale;
  const h = imgH * scale;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

function drawIn(page: PDFPage, img: PDFImage, box: { x: number; y: number; w: number; h: number }, fit: "fit" | "fill") {
  const p = placeImage(box, img.width, img.height, fit);
  if (fit === "fill") page.pushOperators(pushGraphicsState(), rectangle(box.x, box.y, box.w, box.h), clip(), endPath());
  page.drawImage(img, { x: p.x, y: p.y, width: p.w, height: p.h });
  if (fit === "fill") page.pushOperators(popGraphicsState());
}

export async function buildImagePdf(files: File[], opts: ImagePdfOptions, hooks: ImagePdfHooks = {}): Promise<ImagePdfResult> {
  const alive = () => {
    if (hooks.isCurrent && !hooks.isCurrent()) throw new ImagePdfCancelled();
  };
  const normalize = hooks.normalize ?? normalizeImageFile;
  const pdf = await PDFDocument.create();
  const skipped: ImagePdfResult["skipped"] = [];
  const loaded: Loaded[] = [];

  for (let i = 0; i < files.length; i++) {
    alive();
    const f = files[i];
    try {
      const norm = await normalize(f);
      const bytes = new Uint8Array(await norm.arrayBuffer());
      const img = isPng(bytes) ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
      loaded.push({ name: f.name, img, dpi: readImageDpi(bytes) });
    } catch (err) {
      skipped.push({ name: f.name, reason: err instanceof ImageReadError ? err.message : "could not be read" });
    }
    hooks.progress?.(i + 1, files.length);
  }
  if (loaded.length === 0) throw new ImageReadError("None of the images could be read.");

  const margin = MARGIN_PT[opts.margin];
  const pad = opts.pair ? Math.min(6, margin) : 0;
  const pageDims = (img?: Loaded): { w: number; h: number } => {
    if (opts.pageSize === "original") {
      const dpi = opts.originalDpi === "auto" ? (img?.dpi && img.dpi >= 50 && img.dpi <= 1200 ? img.dpi : 96) : opts.originalDpi;
      return { w: (img!.img.width * 72) / dpi, h: (img!.img.height * 72) / dpi };
    }
    if (typeof opts.pageSize === "object") return { w: opts.pageSize.widthPt, h: opts.pageSize.heightPt };
    return PRESET_PT[opts.pageSize];
  };

  const step = opts.pair && opts.pageSize !== "original" ? 2 : 1;
  for (let i = 0; i < loaded.length; i += step) {
    alive();
    const group = loaded.slice(i, i + step);
    if (opts.pageSize === "original" || group.length === 1) {
      const it = group[0];
      const dims = pageDims(it);
      const page = pdf.addPage([dims.w, dims.h]);
      if (opts.pageSize === "original") {
        // The page IS the image (margins would shrink it).
        page.drawImage(it.img, { x: 0, y: 0, width: dims.w, height: dims.h });
      } else {
        drawIn(page, it.img, { x: margin, y: margin, w: dims.w - margin * 2, h: dims.h - margin * 2 }, opts.fit);
      }
      continue;
    }
    const dims = pageDims();
    const page = pdf.addPage([dims.w, dims.h]);
    const innerW = dims.w - margin * 2;
    const innerH = dims.h - margin * 2;
    const cells =
      opts.pair === "vertical"
        ? [
            { x: margin, y: margin, w: innerW / 2, h: innerH },
            { x: margin + innerW / 2, y: margin, w: innerW / 2, h: innerH },
          ]
        : [
            { x: margin, y: margin + innerH / 2, w: innerW, h: innerH / 2 },
            { x: margin, y: margin, w: innerW, h: innerH / 2 },
          ];
    group.forEach((it, k) => {
      const c = cells[k];
      drawIn(page, it.img, { x: c.x + pad, y: c.y + pad, w: c.w - pad * 2, h: c.h - pad * 2 }, opts.fit);
    });
  }
  const stem = files[0].name.replace(/\.[^.]+$/, "");
  stampOutputMetadata(pdf, { title: opts.title ?? stem, fallbackTitle: stem });
  return { bytes: await pdf.save(), pages: pdf.getPageCount(), images: loaded.length, skipped };
}

/** The download name: the image's own name for one image, "first-and-N-more" for several. */
export function pdfNameForImages(names: string[]): string {
  if (names.length === 0) return "images.pdf";
  const stem = (n: string) => n.replace(/\.[^.]+$/, "") || "images";
  return names.length === 1 ? `${stem(names[0])}.pdf` : `${stem(names[0])}-and-${names.length - 1}-more.pdf`;
}

/** A custom page size in inches, millimetres or centimetres → points, or a message saying what is wrong. */
export function parseCustomPageSize(width: string, height: string, unit: "in" | "mm" | "cm"): { ok: true; widthPt: number; heightPt: number } | { ok: false; message: string } {
  const w = Number(width.replace(",", "."));
  const h = Number(height.replace(",", "."));
  if (!width.trim() || !height.trim() || !Number.isFinite(w) || !Number.isFinite(h)) return { ok: false, message: "Enter the page width and height as numbers." };
  const perUnit = unit === "in" ? 72 : unit === "mm" ? 72 / 25.4 : 72 / 2.54;
  const inches = (v: number) => (v * perUnit) / 72;
  if (inches(w) < 1 || inches(h) < 1 || inches(w) > 200 || inches(h) > 200) {
    const [lo, hi] = unit === "in" ? ["1", "200 in"] : unit === "mm" ? ["25.4", "5080 mm"] : ["2.54", "508 cm"];
    return { ok: false, message: `The page must be between ${lo} ${unit === "in" ? "in" : unit} and ${hi} on each side.` };
  }
  return { ok: true, widthPt: w * perUnit, heightPt: h * perUnit };
}

/** True when a GIF has more than one frame (an animation): only its first frame can go in a PDF. */
export function isAnimatedGif(bytes: Uint8Array): boolean {
  const sig = Array.from(bytes.slice(0, 6), (c) => String.fromCharCode(c)).join("");
  if (sig !== "GIF87a" && sig !== "GIF89a") return false;
  let i = 13;
  const flags = bytes[10];
  if (flags & 0x80) i += 3 * (1 << ((flags & 7) + 1));
  let frames = 0;
  while (i < bytes.length) {
    const b = bytes[i++];
    if (b === 0x3b) break;
    if (b === 0x2c) {
      if (++frames > 1) return true;
      const f = bytes[i + 8];
      i += 9;
      if (f & 0x80) i += 3 * (1 << ((f & 7) + 1));
      i++; // LZW minimum code size
      while (i < bytes.length && bytes[i] !== 0) i += bytes[i] + 1;
      i++;
    } else if (b === 0x21) {
      i++; // extension label
      while (i < bytes.length && bytes[i] !== 0) i += bytes[i] + 1;
      i++;
    } else break;
  }
  return false;
}
